import { useEffect, useMemo, useRef, useState } from 'react';
import twemoji from '@twemoji/api';
import {
  CATEGORY_EMOJI,
  CATEGORY_LABEL,
  CATEGORY_TINT,
  type RoundSnapshot,
} from '@hypevault/emoji-decoder';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { gameSounds } from '../lib/gameSounds';

/**
 * Emoji Decoder — full-screen game-show overlay (1920×1080 native).
 *
 * Visual structure mirrors Hype Million: stacked logo top-left, chip row +
 * timer ring across the header, big puzzle card center-stage, right rail
 * for recent winners. Palette stays Emoji Decoder's purple/magenta/gold.
 *
 * Phases:
 *   - guessing : tiles + hint + winners rail
 *   - solved   : winner takeover with confetti + answer + fun fact
 *   - timeout  : reveal answer + red vignette + shake
 *   - cooldown : flip-clock countdown to next puzzle
 */

interface Fields {
  position?: 'fullscreen';
  scale?: number;
  opacity?: number;
}

interface Props {
  streamerId: string;
  snapshot: RoundSnapshot | null;
  fields: Fields;
}

const PALETTE = {
  bg: '#0a0814',
  bgInner: '#1a0d2e',
  purple: '#9333ea',
  purpleDeep: '#6b21a8',
  magenta: '#e879f9',
  gold: '#f59e0b',
  goldDeep: '#d97706',
  green: '#22c55e',
  red: '#ef4444',
  text: '#fff',
  textDim: 'rgba(255,255,255,0.65)',
};

const ANIMATIONS = `
@keyframes ed-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes ed-spotlight { 0% { opacity: 0.3 } 50% { opacity: 0.6 } 100% { opacity: 0.3 } }
@keyframes ed-pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes ed-glow { 0%, 100% { box-shadow: 0 0 24px 4px rgba(147,51,234,0.55) } 50% { box-shadow: 0 0 56px 12px rgba(232,121,249,0.95) } }
@keyframes ed-rise { from { transform: translateY(40px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes ed-tile-slam { 0% { transform: translateY(-180px) scale(0.5); opacity: 0; filter: blur(6px); } 60% { opacity: 1; filter: blur(0); } 78% { transform: translateY(0) scale(1.12); } 100% { transform: translateY(0) scale(1); } }
@keyframes ed-tile-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes ed-tile-explode { 0% { transform: scale(1); } 40% { transform: scale(1.3); filter: brightness(2); } 100% { transform: scale(0.2); opacity: 0; } }
@keyframes ed-tile-shake { 0%, 100% { transform: translateX(0); } 10%, 30%, 50%, 70%, 90% { transform: translateX(-12px); } 20%, 40%, 60%, 80% { transform: translateX(12px); } }
@keyframes ed-confetti { 0% { transform: translateY(-20px) rotate(0); opacity: 1 } 100% { transform: translateY(1080px) rotate(720deg); opacity: 0 } }
@keyframes ed-shake { 0%,100% { transform: translateX(0) } 20% { transform: translateX(-12px) } 40% { transform: translateX(10px) } 60% { transform: translateX(-8px) } 80% { transform: translateX(6px) } }
@keyframes ed-caret { 50% { opacity: 0; } }
@keyframes ed-flip-in { 0% { transform: rotateX(-90deg); opacity: 0; } 100% { transform: rotateX(0); opacity: 1; } }
`;

export function EmojiDecoderWidget({ streamerId, snapshot, fields }: Props) {
  const initial = useRef<RoundSnapshot | null>(snapshot);
  const [latest, setLatest] = useState<RoundSnapshot | null>(snapshot);
  const prevRef = useRef<RoundSnapshot | null>(snapshot);
  useEffect(() => {
    if (initial.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    const ac = new AbortController();
    fetch(`/api/emoji-decoder/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (json: { snapshot?: RoundSnapshot; sound?: typeof DEFAULT_GAME_SOUND_CONFIG } | null) => {
          if (json?.snapshot) setLatest(json.snapshot);
          gameSounds.configure(json?.sound ?? DEFAULT_GAME_SOUND_CONFIG);
        },
      )
      .catch(() => undefined);
    return () => ac.abort();
  }, []);
  useEffect(() => {
    if (snapshot) setLatest(snapshot);
  }, [snapshot]);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = latest;
    if (!latest) return;
    if (!prev || prev.roundId !== latest.roundId) {
      void gameSounds.play('game_start');
      return;
    }
    if (prev.phase !== latest.phase) {
      if (latest.phase === 'solved' && latest.winner) {
        void gameSounds.play('correct');
        void gameSounds.play('winner');
        if (latest.winner.gemReward > 0) {
          setTimeout(() => {
            void gameSounds.play('celebration');
            void gameSounds.play('gems_awarded');
          }, 400);
        }
      } else if (latest.phase === 'timeout') {
        void gameSounds.play('loser');
        void gameSounds.play('reveal');
      }
    }
  }, [latest]);
  void streamerId;

  const opacity = typeof fields.opacity === 'number' ? fields.opacity : 1;
  const scale = typeof fields.scale === 'number' ? fields.scale : 1;

  if (!latest) {
    return <IdleStage opacity={opacity} />;
  }
  return <Stage snapshot={latest} opacity={opacity} scale={scale} />;
}

// ─── Idle ────────────────────────────────────────────────────────────────

function IdleStage({ opacity }: { opacity: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        opacity,
        pointerEvents: 'none',
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Rajdhani:wght@500;600;700&display=swap"
        rel="stylesheet"
      />
    </div>
  );
}

// ─── Stage ───────────────────────────────────────────────────────────────

function Stage({
  snapshot,
  opacity,
  scale,
}: {
  snapshot: RoundSnapshot;
  opacity: number;
  scale: number;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, []);

  const phase = snapshot.phase;
  const isGuessing = phase === 'guessing';
  const isSolved = phase === 'solved';
  const isTimeout = phase === 'timeout';
  const isCooldown = phase === 'cooldown';
  const isCoolingDown = isTimeout || isCooldown;

  const total = snapshot.timeoutAt - snapshot.startedAt;
  const remainingMs = Math.max(0, snapshot.timeoutAt - now);
  const progress = total > 0 ? Math.min(1, remainingMs / total) : 0;
  const remainingSec = Math.ceil(remainingMs / 1000);
  const cooldownEndsAt = snapshot.cooldownEndsAt;
  const cooldownLeft =
    cooldownEndsAt && isCoolingDown ? Math.max(0, Math.ceil((cooldownEndsAt - now) / 1000)) : 0;
  const cooldownTotalMs =
    cooldownEndsAt && isCoolingDown ? Math.max(1, cooldownEndsAt - snapshot.timeoutAt) : 1;
  const cooldownProgress =
    cooldownEndsAt && isCoolingDown
      ? Math.max(0, Math.min(1, (cooldownEndsAt - now) / cooldownTotalMs))
      : 0;
  const ringProgress = isGuessing ? progress : cooldownProgress;
  const ringMode: 'guessing' | 'danger' | 'cooldown' | 'idle' = isGuessing
    ? remainingMs < 10_000
      ? 'danger'
      : 'guessing'
    : isCoolingDown && cooldownEndsAt
      ? 'cooldown'
      : 'idle';
  const headerSeconds = isGuessing ? remainingSec : isCoolingDown ? cooldownLeft : 0;

  const hintRevealed = now >= snapshot.hintRevealAt;
  const tint = CATEGORY_TINT[snapshot.category] ?? PALETTE.purple;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: 1920,
        height: 1080,
        opacity,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        animation: 'ed-fadeIn 400ms ease both',
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
        color: PALETTE.text,
        fontFamily:
          '"Rajdhani","Orbitron",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
        overflow: 'hidden',
      }}
    >
      <style>{ANIMATIONS}</style>
      <link
        href="https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Rajdhani:wght@500;600;700&display=swap"
        rel="stylesheet"
      />

      {/* Stage spotlight */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 50% 0%, ${withAlpha(tint, 0.22)} 0%, transparent 50%)`,
          animation: 'ed-spotlight 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      <Header
        snapshot={snapshot}
        ringProgress={ringProgress}
        ringMode={ringMode}
        seconds={headerSeconds}
        tint={tint}
      />

      <main
        style={{
          position: 'absolute',
          left: 80,
          right: 380,
          top: 200,
          bottom: 80,
          display: 'flex',
          flexDirection: 'column',
          gap: 40,
        }}
      >
        <PuzzleCard snapshot={snapshot} hintRevealed={hintRevealed} tint={tint} />
        <ChatCTA isGuessing={isGuessing} />
      </main>

      <WinnersRail winners={snapshot.recentWinners} />

      {isSolved && snapshot.winner && (
        <WinnerTakeover winner={snapshot.winner} emojis={snapshot.emojis} />
      )}
      {isSolved && <Confetti />}
      {isTimeout && snapshot.reveal && (
        <TimeoutReveal reveal={snapshot.reveal} emojis={snapshot.emojis} />
      )}
      {isTimeout && <LoseVignette />}
      {isCooldown && cooldownLeft > 0 && <CooldownStage seconds={cooldownLeft} />}
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────

function Header({
  snapshot,
  ringProgress,
  ringMode,
  seconds,
  tint,
}: {
  snapshot: RoundSnapshot;
  ringProgress: number;
  ringMode: 'guessing' | 'danger' | 'cooldown' | 'idle';
  seconds: number;
  tint: string;
}) {
  return (
    <header
      style={{
        position: 'absolute',
        top: 40,
        left: 80,
        right: 380,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
      }}
    >
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 12,
            background: `linear-gradient(135deg, ${PALETTE.purple}, ${PALETTE.magenta})`,
            display: 'grid',
            placeItems: 'center',
            fontSize: 36,
            fontWeight: 900,
            boxShadow: `0 8px 32px ${withAlpha(PALETTE.purple, 0.55)}`,
          }}
        >
          🔮
        </div>
        <div>
          <div
            style={{
              fontSize: 14,
              letterSpacing: 4,
              color: PALETTE.magenta,
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            Emoji
          </div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 900,
              letterSpacing: 2,
              lineHeight: 1,
              fontFamily: '"Orbitron","Rajdhani",sans-serif',
              background: `linear-gradient(90deg, ${PALETTE.magenta}, ${PALETTE.gold})`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            DECODER
          </div>
        </div>
      </div>

      {/* Chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Chip
          label="Round"
          value={`#${snapshot.roundNumber}`}
          valueColor={PALETTE.magenta}
          borderColor={tint}
        />
        <Chip
          label="Category"
          value={`${CATEGORY_EMOJI[snapshot.category] ?? '✨'} ${CATEGORY_LABEL[snapshot.category] ?? 'Mixed'}`}
          valueColor={PALETTE.text}
          borderColor={tint}
        />
        <Chip
          label="Prize"
          value={`💎 ${snapshot.gemReward.toLocaleString()}`}
          valueColor={PALETTE.gold}
          borderColor={PALETTE.gold}
        />
      </div>

      {/* Timer */}
      <TimerRing secondsLeft={seconds} progress={ringProgress} mode={ringMode} />
    </header>
  );
}

function Chip({
  label,
  value,
  valueColor,
  borderColor,
}: {
  label: string;
  value: string;
  valueColor: string;
  borderColor: string;
}) {
  return (
    <div
      style={{
        padding: '10px 18px',
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${withAlpha(borderColor, 0.45)}`,
        borderRadius: 999,
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: valueColor }}>{value}</div>
    </div>
  );
}

function TimerRing({
  secondsLeft,
  progress,
  mode,
}: {
  secondsLeft: number;
  progress: number;
  mode: 'guessing' | 'danger' | 'cooldown' | 'idle';
}) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const urgent = mode === 'danger';
  const stops =
    mode === 'danger'
      ? { from: PALETTE.red, to: '#fb7185' }
      : mode === 'cooldown'
        ? { from: PALETTE.purple, to: PALETTE.magenta }
        : mode === 'idle'
          ? { from: 'rgba(255,255,255,0.3)', to: 'rgba(255,255,255,0.3)' }
          : { from: PALETTE.magenta, to: PALETTE.gold };
  return (
    <div
      style={{
        position: 'relative',
        width: 96,
        height: 96,
        display: 'grid',
        placeItems: 'center',
        animation: urgent ? 'ed-pulse 600ms ease-in-out infinite' : undefined,
      }}
    >
      <svg
        width={96}
        height={96}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <defs>
          <linearGradient id="ed-ring-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={stops.from} />
            <stop offset="100%" stopColor={stops.to} />
          </linearGradient>
        </defs>
        <circle cx={48} cy={48} r={r} stroke="rgba(255,255,255,0.12)" strokeWidth={6} fill="none" />
        <circle
          cx={48}
          cy={48}
          r={r}
          stroke="url(#ed-ring-grad)"
          strokeWidth={6}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - progress)}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 200ms linear',
            filter: `drop-shadow(0 0 12px ${stops.from})`,
          }}
        />
      </svg>
      <div
        style={{
          fontSize: 28,
          fontWeight: 900,
          color: urgent ? PALETTE.red : PALETTE.gold,
          fontFamily: '"Orbitron",sans-serif',
          textShadow: `0 0 18px ${urgent ? PALETTE.red : PALETTE.gold}`,
        }}
      >
        {secondsLeft || '—'}
      </div>
    </div>
  );
}

// ─── Puzzle card ─────────────────────────────────────────────────────────

function PuzzleCard({
  snapshot,
  hintRevealed,
  tint,
}: {
  snapshot: RoundSnapshot;
  hintRevealed: boolean;
  tint: string;
}) {
  const emojiHtml = useMemo(
    () => parseEmojis(snapshot.emojis),
    [snapshot.emojis, snapshot.roundId],
  );
  return (
    <div
      style={{
        padding: '40px 48px',
        background: `linear-gradient(180deg, ${withAlpha(PALETTE.bgInner, 0.85)}, ${withAlpha(PALETTE.bg, 0.95)})`,
        border: `2px solid ${withAlpha(tint, 0.55)}`,
        borderRadius: 24,
        boxShadow: '0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
        animation: 'ed-rise 500ms ease both',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 24,
        minHeight: 0,
      }}
    >
      <div
        style={{
          fontSize: 12,
          letterSpacing: 4,
          color: PALETTE.magenta,
          textTransform: 'uppercase',
          fontWeight: 700,
        }}
      >
        For {snapshot.gemReward.toLocaleString()} 💎 — decode this:
      </div>

      <EmojiTiles html={emojiHtml} roundId={snapshot.roundId} phase={snapshot.phase} />

      <HintPill category={snapshot.category} hint={snapshot.hint} revealed={hintRevealed} />
    </div>
  );
}

function EmojiTiles({
  html,
  roundId,
  phase,
}: {
  html: string[];
  roundId: string;
  phase: RoundSnapshot['phase'];
}) {
  const count = html.length;
  const tileSize = count >= 6 ? 130 : count >= 5 ? 150 : count >= 4 ? 170 : 190;
  const animation =
    phase === 'solved'
      ? 'ed-tile-explode 0.9s cubic-bezier(0.4,0,0.2,1) both'
      : phase === 'timeout'
        ? 'ed-tile-shake 0.6s cubic-bezier(0.36,0.07,0.19,0.97) both'
        : undefined;
  return (
    <div
      key={roundId}
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 28,
        flexWrap: 'wrap',
      }}
    >
      {html.map((piece, i) => (
        <div
          key={`${roundId}-${i}`}
          style={{
            position: 'relative',
            width: tileSize,
            height: tileSize,
            borderRadius: 24,
            background: `radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18), transparent 60%), linear-gradient(160deg, ${withAlpha(PALETTE.purple, 0.22)}, ${withAlpha(PALETTE.bgInner, 0.95)})`,
            border: `1px solid ${withAlpha(PALETTE.magenta, 0.45)}`,
            boxShadow: `0 30px 60px -20px rgba(0,0,0,0.6), 0 0 32px -4px ${withAlpha(PALETTE.purple, 0.55)}`,
            display: 'grid',
            placeItems: 'center',
            animation:
              animation ??
              `ed-tile-slam 0.9s cubic-bezier(0.18,0.9,0.2,1.4) both, ed-tile-bob 4.5s ease-in-out infinite 1.2s`,
            animationDelay: animation ? undefined : `${i * 80}ms`,
          }}
        >
          <span
            style={{
              width: '75%',
              height: '75%',
              display: 'grid',
              placeItems: 'center',
            }}
            dangerouslySetInnerHTML={{ __html: piece }}
          />
        </div>
      ))}
    </div>
  );
}

function HintPill({
  category,
  hint,
  revealed,
}: {
  category: RoundSnapshot['category'];
  hint: string;
  revealed: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        filter: revealed ? 'none' : 'blur(14px)',
        opacity: revealed ? 1 : 0.4,
        transition: 'filter 0.6s ease, opacity 0.6s ease',
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 18,
          padding: '14px 30px',
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${withAlpha(PALETTE.gold, 0.5)}`,
          borderRadius: 999,
          backdropFilter: 'blur(8px)',
          boxShadow: `0 0 28px -4px ${PALETTE.gold}`,
        }}
      >
        <span style={{ fontSize: 28 }}>{CATEGORY_EMOJI[category]}</span>
        <span
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: 5,
            color: PALETTE.gold,
            textShadow: `0 0 14px ${PALETTE.gold}`,
            textTransform: 'uppercase',
          }}
        >
          {hint || CATEGORY_LABEL[category]}
        </span>
      </div>
    </div>
  );
}

function ChatCTA({ isGuessing }: { isGuessing: boolean }) {
  if (!isGuessing) return null;
  return (
    <div
      style={{
        textAlign: 'center',
        fontFamily: '"Rajdhani",sans-serif',
        fontSize: 22,
        fontWeight: 600,
        letterSpacing: 4,
        textTransform: 'uppercase',
        color: PALETTE.textDim,
      }}
    >
      Type your answer in chat
      <span style={{ animation: 'ed-caret 1s steps(2) infinite' }}>_</span>
    </div>
  );
}

// ─── Winners rail ────────────────────────────────────────────────────────

function WinnersRail({ winners }: { winners: RoundSnapshot['recentWinners'] }) {
  return (
    <aside
      style={{
        position: 'absolute',
        top: 40,
        right: 40,
        bottom: 40,
        width: 300,
        padding: '24px 20px',
        background: `linear-gradient(180deg, ${withAlpha(PALETTE.bgInner, 0.85)}, ${withAlpha(PALETTE.bg, 0.95)})`,
        border: `2px solid ${withAlpha(PALETTE.purple, 0.45)}`,
        borderRadius: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        boxShadow: '0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
    >
      <div
        style={{
          fontSize: 14,
          letterSpacing: 4,
          color: PALETTE.magenta,
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 800,
          marginBottom: 4,
        }}
      >
        Recent Solves
      </div>
      {winners.length === 0 ? (
        <div
          style={{
            margin: 'auto 0',
            textAlign: 'center',
            fontFamily: '"Rajdhani",sans-serif',
            fontSize: 16,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.25)',
            padding: '0 8px',
          }}
        >
          First solve drops here.
        </div>
      ) : (
        winners.map((w, i) => (
          <div
            key={`${w.solvedAt}-${w.username}`}
            style={{
              position: 'relative',
              padding: '10px 14px',
              borderRadius: 12,
              background:
                i === 0
                  ? `linear-gradient(90deg, ${withAlpha(PALETTE.magenta, 0.18)}, ${withAlpha(PALETTE.purple, 0.08)})`
                  : 'rgba(255,255,255,0.04)',
              border: `1px solid ${
                i === 0 ? withAlpha(PALETTE.magenta, 0.55) : 'rgba(255,255,255,0.06)'
              }`,
              opacity: 1 - i * 0.14,
              animation: i === 0 ? 'ed-glow 2.4s ease-in-out infinite' : undefined,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: '"Orbitron",sans-serif',
                fontSize: 14,
                fontWeight: 800,
                color: PALETTE.gold,
                textShadow: `0 0 8px ${PALETTE.gold}`,
              }}
            >
              <span>🏆</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {w.username}
              </span>
            </div>
            <div
              style={{
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: PALETTE.textDim,
              }}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
                dangerouslySetInnerHTML={{ __html: parseEmojis(w.emojis).join('') }}
              />
              <span>=</span>
              <span
                style={{
                  color: PALETTE.text,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {w.answer}
              </span>
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                fontWeight: 800,
                color: PALETTE.magenta,
              }}
            >
              +{w.gemReward.toLocaleString()} 💎
            </div>
          </div>
        ))
      )}
    </aside>
  );
}

// ─── Takeovers ───────────────────────────────────────────────────────────

function WinnerTakeover({
  winner,
  emojis,
}: {
  winner: NonNullable<RoundSnapshot['winner']>;
  emojis: string;
}) {
  const emojiHtml = useMemo(() => parseEmojis(emojis).join(''), [emojis]);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        zIndex: 8,
        background: `radial-gradient(circle at 50% 50%, ${withAlpha(PALETTE.magenta, 0.32)}, rgba(0,0,0,0.92) 70%)`,
        backdropFilter: 'blur(10px)',
        animation: 'ed-fadeIn 500ms ease both',
      }}
    >
      <div style={{ textAlign: 'center', padding: '0 48px', fontFamily: '"Orbitron",sans-serif' }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: 14,
            color: PALETTE.gold,
            textShadow: `0 0 18px ${PALETTE.gold}`,
            marginBottom: 12,
          }}
        >
          SOLVED BY
        </div>
        <div
          style={{
            fontSize: 120,
            fontWeight: 900,
            letterSpacing: 8,
            color: PALETTE.text,
            textShadow: `0 0 36px ${PALETTE.gold}, 0 0 72px ${PALETTE.magenta}`,
            lineHeight: 1,
            animation: 'ed-pulse 1.4s ease-in-out infinite',
          }}
        >
          {winner.username}
        </div>
        <div
          style={{
            marginTop: 36,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 28,
            fontSize: 52,
            fontWeight: 800,
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: emojiHtml }} />
          <span style={{ color: PALETTE.textDim, fontWeight: 600 }}>=</span>
          <span style={{ color: PALETTE.text, letterSpacing: 4 }}>{winner.answer}</span>
        </div>
        <div
          style={{
            marginTop: 24,
            fontSize: 60,
            fontWeight: 900,
            color: PALETTE.gold,
            textShadow: `0 0 28px ${PALETTE.gold}`,
          }}
        >
          +{winner.gemReward.toLocaleString()} 💎
        </div>
        {winner.funFact && (
          <div
            style={{
              marginTop: 24,
              fontFamily: '"Rajdhani",sans-serif',
              fontWeight: 600,
              fontSize: 24,
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 1100,
              lineHeight: 1.4,
              margin: '24px auto 0',
            }}
          >
            💡 {winner.funFact}
          </div>
        )}
      </div>
    </div>
  );
}

function TimeoutReveal({
  reveal,
  emojis,
}: {
  reveal: NonNullable<RoundSnapshot['reveal']>;
  emojis: string;
}) {
  const emojiHtml = useMemo(() => parseEmojis(emojis).join(''), [emojis]);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        zIndex: 8,
        background: `radial-gradient(circle at 50% 50%, ${withAlpha(PALETTE.red, 0.32)}, rgba(0,0,0,0.94) 70%)`,
        backdropFilter: 'blur(10px)',
        animation: 'ed-fadeIn 500ms ease both',
      }}
    >
      <div style={{ textAlign: 'center', padding: '0 48px', fontFamily: '"Orbitron",sans-serif' }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: 14,
            color: PALETTE.red,
            textShadow: `0 0 18px ${PALETTE.red}`,
            marginBottom: 24,
          }}
        >
          😔 NOBODY GOT IT
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 28,
            fontSize: 52,
            fontWeight: 800,
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: emojiHtml }} />
          <span style={{ color: PALETTE.textDim, fontWeight: 600 }}>=</span>
          <span style={{ color: PALETTE.text, letterSpacing: 4 }}>{reveal.answer}</span>
        </div>
        {reveal.funFact && (
          <div
            style={{
              marginTop: 32,
              fontFamily: '"Rajdhani",sans-serif',
              fontWeight: 600,
              fontSize: 24,
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 1100,
              lineHeight: 1.4,
              margin: '32px auto 0',
            }}
          >
            💡 {reveal.funFact}
          </div>
        )}
      </div>
    </div>
  );
}

function CooldownStage({ seconds }: { seconds: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        zIndex: 8,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(4px)',
        animation: 'ed-fadeIn 400ms ease both',
      }}
    >
      <div style={{ textAlign: 'center', fontFamily: '"Orbitron",sans-serif' }}>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: 8,
            color: PALETTE.textDim,
            marginBottom: 18,
          }}
        >
          NEXT PUZZLE IN
        </div>
        <FlipDigit n={seconds} />
      </div>
    </div>
  );
}

function FlipDigit({ n }: { n: number }) {
  return (
    <div
      key={n}
      style={{
        position: 'relative',
        display: 'inline-grid',
        placeItems: 'center',
        width: 240,
        height: 280,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
        border: `1px solid ${withAlpha(PALETTE.purple, 0.4)}`,
        borderRadius: 24,
        boxShadow: `0 30px 80px -20px rgba(0,0,0,0.6), 0 0 60px -10px ${withAlpha(PALETTE.purple, 0.6)}`,
        fontFamily: '"Orbitron",monospace',
        animation: 'ed-flip-in 0.4s ease-out both',
      }}
    >
      <span
        style={{
          fontSize: 200,
          fontWeight: 900,
          lineHeight: 1,
          color: PALETTE.text,
          textShadow: `0 0 32px ${PALETTE.magenta}, 0 0 64px ${PALETTE.purple}`,
        }}
      >
        {n}
      </span>
    </div>
  );
}

// ─── FX ──────────────────────────────────────────────────────────────────

function Confetti() {
  const pieces = Array.from({ length: 80 });
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 9 }}>
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 800;
        const duration = 1800 + Math.random() * 1400;
        const colorIdx = i % 3;
        const color =
          colorIdx === 0 ? PALETTE.gold : colorIdx === 1 ? PALETTE.magenta : PALETTE.purple;
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: 0,
              left: `${left}%`,
              width: 10,
              height: 14,
              background: color,
              animation: `ed-confetti ${duration}ms linear ${delay}ms forwards`,
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}

function LoseVignette() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: `radial-gradient(ellipse at center, transparent 30%, ${withAlpha(PALETTE.red, 0.55)} 100%)`,
        animation: 'ed-shake 600ms ease 1',
        pointerEvents: 'none',
        zIndex: 4,
      }}
    />
  );
}

// ─── Utility ─────────────────────────────────────────────────────────────

// Split by Unicode grapheme cluster — keeps ZWJ sequences like 👨‍🍳 / 👨‍👩‍👧
// as one tile instead of producing blank squares for the ZWJ / variation
// selectors. Intl.Segmenter is in every Chromium since v87, including OBS.
const GRAPHEME_SEGMENTER =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? new Intl.Segmenter('en', { granularity: 'grapheme' })
    : null;

function parseEmojis(s: string): string[] {
  const out: string[] = [];
  const segments: string[] = GRAPHEME_SEGMENTER
    ? [...GRAPHEME_SEGMENTER.segment(s)].map((g) => g.segment)
    : [...s];
  for (const seg of segments) {
    if (!seg.trim()) continue;
    // Skip stray variation selectors / ZWJs that escaped segmentation —
    // these would render as a blank tile otherwise.
    if (/^[‍️⃣]+$/u.test(seg)) continue;
    const html = twemoji.parse(seg, {
      folder: 'svg',
      ext: '.svg',
      base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/',
      attributes: () => ({ style: 'width:100%;height:100%;' }),
    });
    out.push(html || seg);
  }
  return out;
}

/** Add alpha to a 6-char hex color. Accepts rgba() pass-through unchanged. */
function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('rgba')) return color;
  if (color.startsWith('#') && color.length === 7) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return color;
}
